import { X } from "@phosphor-icons/react";
import { Portal } from "@radix-ui/react-portal";
import clsx from "clsx";
import { useAtom } from "jotai";
import { noop } from "lodash-es";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useId, useRef, useState } from "react";
import ReactFocusLock from "react-focus-lock";
import { match } from "ts-pattern";

import { queueAtom } from "~/atoms/queue";

import { ModalContext } from "./ModalContext";

const MotionFocusLock = motion.create(ReactFocusLock);

export type ModalSize = "sm" | "md" | "lg";

export type ModalType = "dialog" | "drawer" | "unstyled";

export const Modal: React.FC<{
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  type?: ModalType;
}> = ({ isOpen, onClose, children, size = "md", type = "dialog" }) => {
  const id = useId();
  const [key, setKey] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const [queue, setQueue] = useAtom(queueAtom);

  const prevIsOpen = useRef(isOpen);

  useEffect(() => {
    if (prevIsOpen.current !== isOpen) {
      if (isOpen) {
        setKey((key) => key + 1);
        setQueue((prev) => [id, ...prev]);
      } else {
        setQueue((prev) => prev.filter((item) => item !== id));
      }
      prevIsOpen.current = isOpen;
    }
  }, [isOpen, id, setQueue]);

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key={key} // use a key to reset the modal contents as soon as it's closed
            className={clsx(
              // use `modal-open` to prevent the page from scrolling
              "modal-open fixed inset-0 z-30 flex flex-col bg-black/40 transition-none",
              type !== "unstyled" &&
                match(type)
                  .with(
                    "dialog",
                    () =>
                      "sm:p-8 md:p-10 [&:not(:has([data-dialog-footer]))]:overflow-auto",
                  )
                  .with("drawer", () => "sm:pt-8 md:pt-10")
                  .exhaustive(),
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              pointerEvents: "none",
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                onClose?.();
              }
            }}
          >
            <MotionFocusLock
              initial={{ y: prefersReducedMotion ? 0 : 20 }}
              animate={{ y: 0 }}
              exit={{ y: prefersReducedMotion ? 0 : 10 }}
              className={match(type)
                .with(
                  "unstyled",
                  () => "pointer-events-none absolute inset-8 flex md:inset-10",
                )
                .with("dialog", "drawer", (type) =>
                  clsx(
                    "group/modal relative mx-auto flex w-full flex-col bg-base-100 shadow",
                    match(type)
                      .with("dialog", () =>
                        clsx(
                          "has-[[data-dialog-footer]]:overflow-hidden max-sm:grow sm:rounded-box sm:border md:my-auto",
                          match(size)
                            .with("sm", () => "sm:max-w-md")
                            .with("md", () => "sm:max-w-xl")
                            .with("lg", () => "sm:max-w-2xl")
                            .exhaustive(),
                        ),
                      )
                      .with("drawer", () => "grow overflow-hidden sm:border-t")
                      .exhaustive(),
                  ),
                )
                .exhaustive()}
            >
              {onClose && (
                <button
                  onClick={onClose}
                  className={clsx(
                    "btn btn-square btn-ghost btn-sm absolute right-2 top-2 group-has-[[data-dialog-header]]/modal:right-4 group-has-[[data-dialog-header]]/modal:top-4",
                    type === "unstyled" && "hidden",
                  )}
                  ref={() => {
                    const handleKeyDown = (event: KeyboardEvent) => {
                      if (event.key === "Escape" && queue[0] === id) {
                        onClose();
                      }
                    };

                    window.addEventListener("keydown", handleKeyDown);
                    return () => {
                      window.removeEventListener("keydown", handleKeyDown);
                    };
                  }}
                >
                  <X weight="bold" className="size-5" />
                </button>
              )}
              <ModalContext value={{ closeModal: onClose ?? noop }}>
                {children}
              </ModalContext>
            </MotionFocusLock>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
};
